import { z } from 'zod';

/**
 * Closed palette of accent colors a category can carry. Cerrada a propósito:
 *  - Mantiene la UI consistente (el card border-l, los chips, los charts)
 *  - Permite que Tailwind tree-shake las clases, ya que las usamos como
 *    literales estáticos en `category-colors.ts` (la JIT no descubre clases
 *    armadas dinámicamente).
 *  - Evita CSS arbitrario que un admin pudiera ingresar y romper la marca.
 *
 * Los nombres son los que ya usa la paleta interna de Tailwind v4, así
 * cualquier consumer puede componer `bg-${color}-500/10` y similar sin
 * mapeo intermedio.
 */
export const categoryColorSchema = z.enum([
  'slate',
  'red',
  'orange',
  'amber',
  'yellow',
  'lime',
  'emerald',
  'teal',
  'cyan',
  'sky',
  'blue',
  'violet',
  'fuchsia',
  'rose',
]);
export type CategoryColor = z.infer<typeof categoryColorSchema>;

export const categorySchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(80),
  slug: z.string().min(1).max(80),
  icon: z.string().min(1).max(8).default('📁'),
  color: categoryColorSchema.default('slate'),
  description: z.string().max(500).nullable().default(null),
  order: z.number().int().nonnegative().default(0),
  isActive: z.boolean().default(true),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  createdBy: z.string().email().nullable().default(null),
});
export type Category = z.infer<typeof categorySchema>;

/**
 * Derive a URL-friendly slug from a free-form category name. Lossy on
 * accents/punctuation, but stable: same input → same output. Used at read
 * time to backfill legacy GAS-era category docs which never wrote `slug`
 * (the GAS frontend keyed everything by name; see index.html:2920-2932).
 *
 * Stays in this module rather than `lib/` so tests and consumers can import
 * it without dragging server-only deps. Keeping the implementation tiny
 * intentionally — anything more elaborate (e.g. uniqueness checks across
 * the collection) belongs in a write-time helper, not a read-side defaulter.
 */
function deriveSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
}

/**
 * Title-case a slug-style id ("github-repositories" → "Github repositories").
 * Used as the `name` fallback for docs that have a slug-style id but no
 * `name` field (a few rows in the `categories` collection were seeded
 * manually via the Firestore console with only `icon` and `id=slug`).
 */
function nameFromId(id: string): string {
  const lower = id.replace(/-/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Sentinel timestamp used as the last-resort fallback when a legacy doc has
 * no usable `createdAt` AND no `updatedAt`. Stable, safe to compare with
 * `localeCompare`, and visibly oldest so admins can spot rows that need
 * manual cleanup in the Firestore console.
 */
const LEGACY_FALLBACK_AT = '1970-01-01T00:00:00.000Z';

/**
 * Coerce a Firestore-side timestamp value into a usable ISO string, falling
 * back to `null` if nothing recognizable is found. Tolerates:
 *  - native ISO strings (already ok)
 *  - epoch-millis numbers (legacy GAS api_tokens style)
 *  - Firestore `Timestamp` objects with a `toDate()` method
 *  - plain `{seconds, nanoseconds}` shapes from REST exports
 */
function coerceTimestamp(v: unknown): string | null {
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return v;
    const ms = Number(v);
    if (Number.isFinite(ms) && ms > 0) return new Date(ms).toISOString();
    return null;
  }
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) {
    return new Date(v).toISOString();
  }
  if (v && typeof v === 'object') {
    const obj = v as Record<string, unknown>;
    if (typeof obj.toDate === 'function') {
      try {
        const d = (obj.toDate as () => Date)();
        if (d instanceof Date && !Number.isNaN(d.getTime())) return d.toISOString();
      } catch {
        // fall through to other strategies
      }
    }
    if (typeof obj.seconds === 'number') {
      return new Date(obj.seconds * 1000).toISOString();
    }
  }
  return null;
}

/**
 * Legacy-tolerant read schema. Built as a `transform` rather than a
 * `preprocess` so we have FULL control over every field and the parse
 * cannot fail except when `id` is genuinely missing. Every Firestore doc
 * that lands here surfaces as a renderable Category — visibly malformed
 * fields fall back to safe defaults (📁 icon, 0 order, 1970 timestamp,
 * id-derived name) instead of disappearing from the listing.
 *
 * Why this matters: phase 9 + the first phase 10 patch tried to bridge
 * specific gaps (slug, updatedAt) but kept failing in production because
 * different legacy paths produced different gaps — some docs had no
 * `name`, some had a number where a string was expected, some had
 * `createdAt` as a Firestore Timestamp object. Listing every variant in
 * a preprocess is whack-a-mole; rewriting as a defensive transform
 * collapses all those branches into one.
 *
 * Writes still go through the strict `categorySchema` /
 * `createCategorySchema` so new data never reaches this fallback path.
 */
export const categoryReadSchema = z.unknown().transform((raw, ctx) => {
  if (typeof raw !== 'object' || raw === null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Document is not an object' });
    return z.NEVER;
  }
  const obj = raw as Record<string, unknown>;

  const id = typeof obj.id === 'string' && obj.id.length > 0 ? obj.id : '';
  if (!id) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Document is missing an id' });
    return z.NEVER;
  }

  const rawName = typeof obj.name === 'string' && obj.name.length > 0 ? obj.name : nameFromId(id);
  const name = rawName.slice(0, 80);

  const rawSlug =
    typeof obj.slug === 'string' && obj.slug.length > 0 ? obj.slug : deriveSlug(name) || id;
  const slug = rawSlug.slice(0, 80);

  const icon =
    typeof obj.icon === 'string' && obj.icon.length > 0 && obj.icon.length <= 8 ? obj.icon : '📁';

  // Color must be one of the canonical palette values; anything outside
  // (legacy GAS docs that wrote arbitrary hex strings, or rows seeded
  // before the field existed) falls back to `slate` so the card border
  // stays neutral instead of breaking Tailwind's tree-shake.
  const colorParsed = categoryColorSchema.safeParse(obj.color);
  const color: CategoryColor = colorParsed.success ? colorParsed.data : 'slate';

  const description =
    typeof obj.description === 'string' && obj.description.length > 0
      ? obj.description.slice(0, 500)
      : null;

  // order: number, string-of-number, or anything → 0. Negative values clamp.
  const orderInput =
    typeof obj.order === 'number'
      ? obj.order
      : typeof obj.order === 'string'
        ? Number(obj.order)
        : Number.NaN;
  const order = Number.isFinite(orderInput) ? Math.max(0, Math.floor(orderInput)) : 0;

  const isActive = typeof obj.isActive === 'boolean' ? obj.isActive : obj.isActive !== false;

  const createdAt =
    coerceTimestamp(obj.createdAt) ?? coerceTimestamp(obj.updatedAt) ?? LEGACY_FALLBACK_AT;
  const updatedAt = coerceTimestamp(obj.updatedAt) ?? createdAt;

  // createdBy must be a valid email or null. Anything that doesn't look
  // like an email becomes null so it still fits the canonical schema.
  const createdByRaw = obj.createdBy;
  const createdBy =
    typeof createdByRaw === 'string' && /.+@.+\..+/.test(createdByRaw) ? createdByRaw : null;

  const result: Category = {
    id,
    name,
    slug,
    icon,
    color,
    description,
    order,
    isActive,
    createdAt,
    updatedAt,
    createdBy,
  };
  return result;
});

export const createCategorySchema = z.object({
  name: z.string().min(1).max(80),
  slug: z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9-]+$/, 'Slug must be lowercase alphanumeric with dashes'),
  icon: z.string().min(1).max(8).optional(),
  color: categoryColorSchema.optional(),
  description: z.string().max(500).optional(),
  order: z.number().int().nonnegative().optional(),
});
export type CreateCategoryInput = z.infer<typeof createCategorySchema>;

export const updateCategorySchema = createCategorySchema.partial().extend({
  isActive: z.boolean().optional(),
});
export type UpdateCategoryInput = z.infer<typeof updateCategorySchema>;

export const DEFAULT_CATEGORIES = [
  { name: 'GitHub Repositories', slug: 'github-repositories', icon: '📁' },
  { name: 'Databases', slug: 'databases', icon: '🗄️' },
  { name: 'Dashboards', slug: 'dashboards', icon: '📊' },
  { name: 'APIs', slug: 'apis', icon: '🔌' },
  { name: 'Documentation', slug: 'documentation', icon: '📖' },
  { name: 'Data Tools', slug: 'data-tools', icon: '🛠️' },
] as const;
